import type {
  ConversationalFormOption,
  ConversationalFormStep,
} from '@karehero/models';
import type { ChoiceStyle } from '@karehero/llama/pwa/pages/ConversationalFormPage/ConversationalFormPage';

import { AGE_BRACKET_ID, UNDER_18 } from 'config/benefitsCalculatorV2Steps';
import {
  BEAT_STEPS,
  CALL_CHOICES,
  questionStep,
  startChoices,
  TALK_STEPS,
  under18Step,
  WELCOME_SCREENS,
  type BeatStep,
} from 'config/onboardingV4/beatSteps';
import {
  OPENING,
  type OnboardingV4Beat,
  type OnboardingV4Flow,
} from 'config/onboardingV4/flows';
import { triageQuestions, type TriageQuestion } from 'config/triageQuestions';
import * as copy from 'config/onboardingV4/copy';
import {
  isAnswer,
  needsNoHelp,
  nextQuestion,
  type Knowledge,
} from 'utils/triageKnowledge';
import {
  ageAgainKey,
  correctingKey,
  heardKey,
  helpWithKey,
  isHelpWithAskedIn,
  under18Key,
  type OnboardingV4Progress,
} from 'utils/onboardingV4Progress';
import { phrase, type Person } from 'utils/person';

// Which step onboarding v4 offers at each beat, once Keni has asked, and how
// its answers are laid out. What each step asks is config/onboardingV4/beatSteps.

/** More answers than this are offered under the question rather than as chips. */
const MAX_CHIPS = 3;

/** The beats before anyone is asked about who is cared for, where no age can stop them. */
const OPENING_BEATS: readonly OnboardingV4Beat['kind'][] = OPENING.map(
  (beat) => beat.kind,
);

/** A step in the chat, with how its answers are laid out where the step says. */
export type OnboardingV4Step = ConversationalFormStep &
  Pick<BeatStep, 'choiceStyle'>;

/** Asked again after the under 18 stop, whether or not the flow asks it. */
export const [AGE_QUESTION] = triageQuestions([AGE_BRACKET_ID]);

/** What cuts in ahead of the beat, whichever beat it comes in: the under 18 stop, or the age asked again after it. Each is said once by its key. */
export type Interjection =
  | { kind: 'stop'; key: string; step: BeatStep }
  | {
      kind: 'age-again';
      key: string;
      step: BeatStep;
      question: TriageQuestion;
    };

export interface InterjectionContext {
  beat: OnboardingV4Beat;
  knowledge: Knowledge;
  progress: OnboardingV4Progress;
  person: Person;
}

/** The interjection in force: past the opening, an age known to be under 18 stops them, unless it is being asked again. */
export const interjectionFor = ({
  beat,
  knowledge,
  progress,
  person,
}: InterjectionContext): Interjection | undefined => {
  if (progress.ageAgain?.kind === 'asking') {
    return {
      kind: 'age-again',
      key: ageAgainKey(progress),
      step: questionStep(AGE_QUESTION, person),
      question: AGE_QUESTION,
    };
  }
  if (OPENING_BEATS.includes(beat.kind)) return undefined;
  if (!isAnswer(knowledge, AGE_BRACKET_ID, UNDER_18)) return undefined;
  return { kind: 'stop', key: under18Key(progress), step: under18Step(person) };
};

export interface QuestionContext {
  interjection: Interjection | undefined;
  beat: OnboardingV4Beat | null;
  questions: readonly TriageQuestion[];
  knowledge: Knowledge;
  progress: OnboardingV4Progress;
}

// A question asked stays the question until it is answered, even if what
// they said answers it in the meantime.
const pendingQuestion = ({ questions, knowledge, progress }: QuestionContext) =>
  questions.find(
    (q) =>
      progress.asked.includes(q.id) &&
      !knowledge.answers[q.id] &&
      !knowledge.saidInOwnWords.includes(q.id),
  ) ?? null;

/** The question being asked: the age again, none under the stop, else the questions beat's pending or next one. */
export const questionFor = (
  context: QuestionContext,
): TriageQuestion | null => {
  const { interjection, beat, questions, knowledge } = context;
  if (interjection) {
    return interjection.kind === 'age-again' ? interjection.question : null;
  }
  if (beat?.kind !== 'questions') return null;
  return (
    pendingQuestion(context) ?? nextQuestion(questions, beat.mode, knowledge)
  );
};

export interface StepContext {
  flow: OnboardingV4Flow;
  beat: OnboardingV4Beat;
  progress: OnboardingV4Progress;
  person: Person;
  /** The question being asked, while the beat is the questions. */
  question: TriageQuestion | null;
  knowledge: Knowledge;
  isVoiceOn: boolean;
  isFileOn: boolean;
}

type Inputs = Pick<StepContext, 'isVoiceOn' | 'isFileOn'>;

/** A step as designed, in the chat: voice where it has it, files wherever they are on, never a placeholder. */
const chatStep = (
  { hasVoice, ...step }: BeatStep,
  { isVoiceOn, isFileOn }: Inputs,
): OnboardingV4Step => ({
  presentation: 'chat',
  ...step,
  placeholder: '',
  isFileEnabled: isFileOn,
  ...(hasVoice && { isVoiceEnabled: isVoiceOn }),
});

/** The step a beat offers once Keni has asked, or nothing while she is still to. */
export const stepFor = (context: StepContext): OnboardingV4Step | undefined => {
  const { flow, beat, progress, person, question, knowledge } = context;
  const { heardRound } = progress;
  const isAsked = (id: string) => progress.asked.includes(id);
  const asked = (id: string, step: BeatStep) =>
    isAsked(id) ? chatStep(step, context) : undefined;
  const interjection = interjectionFor(context);
  if (interjection) return asked(interjection.key, interjection.step);
  switch (beat.kind) {
    case 'welcome':
      return {
        ...WELCOME_SCREENS.welcome,
        imageSrc: flow.isKeniShown ? copy.KENI_IMAGE_SRC : undefined,
      };
    case 'consent':
      return flow.consentStyle === 'chat'
        ? asked('ai-consent', BEAT_STEPS.consent)
        : WELCOME_SCREENS.consent;
    case 'who':
      return asked('who', BEAT_STEPS.who);
    case 'talk':
      return progress.readingFrom
        ? undefined
        : asked('talk', TALK_STEPS[flow.talkOpening]);
    case 'questions':
      return question
        ? asked(question.id, questionStep(question, person))
        : undefined;
    case 'heard':
      return (
        asked(helpWithKey(heardRound), {
          ...BEAT_STEPS.helpWith,
          id: helpWithKey(heardRound),
        }) ??
        asked(correctingKey(heardRound), {
          ...BEAT_STEPS.correcting,
          id: correctingKey(heardRound),
        }) ??
        asked(heardKey(heardRound), {
          ...BEAT_STEPS.heard,
          id: heardKey(heardRound),
        })
      );
    case 'help':
      return beat.asks ? asked('help', BEAT_STEPS.help) : undefined;
    case 'need':
      return asked('need', BEAT_STEPS.need);
    case 'gather':
      return asked('gather', BEAT_STEPS.gather);
    case 'phone':
      return asked('phone', BEAT_STEPS.phone);
    case 'start':
      return asked('start', {
        ...BEAT_STEPS.start,
        options: startOptions(beat, knowledge, person),
      });
    default:
      return undefined;
  }
};

/** What they are answering when they say something, as it was asked; read with what they say. */
export const askFor = ({
  beat,
  flow,
  question,
  person,
  progress,
  interjection,
}: Pick<StepContext, 'flow' | 'question' | 'person'> & {
  beat: OnboardingV4Beat | null;
  progress?: OnboardingV4Progress;
  interjection?: Interjection;
}): string => {
  if (interjection) return interjection.step.ask;
  if (question) return phrase(question.ask, person);
  switch (beat?.kind) {
    case 'talk':
      return TALK_STEPS[flow.talkOpening].ask;
    case 'heard':
      if (progress && isHelpWithAskedIn(progress, progress.heardRound)) {
        return BEAT_STEPS.helpWith.ask;
      }
      return BEAT_STEPS.correcting.ask;
    case 'help':
      return BEAT_STEPS.help.ask;
    case 'need':
      return BEAT_STEPS.need.ask;
    case 'gather':
      return BEAT_STEPS.gather.ask;
    case 'phone':
      return BEAT_STEPS.phone.ask;
    default:
      return TALK_STEPS.prompted.ask;
  }
};

/** Where they could start: booking a call, where the beat books one, else from what is known. */
export const startOptions = (
  beat: Extract<OnboardingV4Beat, { kind: 'start' }>,
  knowledge: Knowledge,
  person: Person,
): ConversationalFormOption[] =>
  beat.booksCall
    ? CALL_CHOICES
    : startChoices(person, { needsHelp: !needsNoHelp(knowledge) });

/** As the step says, else a choice that cannot be typed goes with the question, as do more answers than fit as chips. */
export const choiceStyleFor = (
  beat: OnboardingV4Beat | null,
  flow: OnboardingV4Flow,
  step: Pick<OnboardingV4Step, 'options' | 'choiceStyle'> | undefined,
): ChoiceStyle => {
  if (step?.choiceStyle) return step.choiceStyle;
  if (beat?.kind === 'start') return flow.startChoiceStyle;
  // Agreeing is a choice from a group, with its explanations under it.
  if (beat?.kind === 'consent') return 'attached';
  if (flow.choiceStyle !== 'chips') return flow.choiceStyle;
  if (beat?.kind === 'who') return 'attached';
  return (step?.options?.length ?? 0) > MAX_CHIPS ? 'attached' : 'chips';
};

/** The section named above the chat, where the flow names one. */
export const sectionFor = (
  beat: OnboardingV4Beat | null,
  person: Person,
  flow: OnboardingV4Flow,
): string | undefined => {
  // The 2.0 design has no section names, only the logo.
  if (flow.talkOpening === 'open') return undefined;
  if (!beat || beat.kind === 'welcome' || beat.kind === 'consent') {
    return undefined;
  }
  if (beat.kind === 'help' || beat.kind === 'start') {
    return copy.SECTION.howWeHelp;
  }
  return person.isSelf ? copy.SECTION.aboutYou : copy.SECTION.aboutThem;
};
